import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { trackSoundToggle, trackFeatureUsage } from '../../utils/analytics';
import './SoundToggle.css';

/**
 * Sound toggle button component with tooltip
 * @param {boolean} enabled - Whether sound is enabled
 * @param {function} onToggle - Toggle handler
 * @param {string} label - Label for accessibility (e.g., "Whale trades", "Liquidations")
 * @param {string} tooltipContent - HTML content for tooltip (optional)
 * @param {boolean} hideTooltipOnMobile - Hide tooltip on mobile devices (optional)
 * @param {boolean} millionAlertEnabled - Whether $1M+ alert is enabled (optional)
 * @param {function} onMillionAlertToggle - Handler for toggling $1M+ alert (optional)
 * @param {boolean} showMillionAlertToggle - Whether to show the $1M+ toggle in tooltip (optional)
 */
function SoundToggle({
  enabled,
  onToggle,
  label,
  tooltipContent,
  hideTooltipOnMobile,
  millionAlertEnabled,
  onMillionAlertToggle,
  showMillionAlertToggle
}) {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const hideTimeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  // Detect touch device on mount
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // Close tooltip when clicking outside (for touch devices)
  useEffect(() => {
    if (!showTooltip || !isTouchDevice) return;

    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowTooltip(false);
      }
    };

    // Small delay to avoid immediate close on same tap
    const timeoutId = setTimeout(() => {
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showTooltip, isTouchDevice]);

  // Show tooltip immediately, hide with delay (desktop only)
  const handleMouseEnter = useCallback(() => {
    if (isTouchDevice) return;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setShowTooltip(true);
  }, [isTouchDevice]);

  const handleMouseLeave = useCallback(() => {
    if (isTouchDevice) return;
    // Delay hiding to allow mouse to move to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
    }, 150);
  }, [isTouchDevice]);

  // Toggle tooltip on tap (touch devices)
  const handleWrapperClick = useCallback((e) => {
    if (isTouchDevice) {
      e.preventDefault();
      e.stopPropagation();
      setShowTooltip(prev => !prev);
    }
  }, [isTouchDevice]);

  const handleToggle = () => {
    const soundType = label?.toLowerCase().includes('whale') ? 'whale_trades' : 'liquidations';
    const newState = !enabled;

    // Track sound toggle with detailed context (sends separate sound_enabled/sound_disabled events)
    trackSoundToggle(newState, soundType);

    onToggle();
  };

  const handleMillionAlertToggle = (e) => {
    e.stopPropagation(); // Prevent main toggle from firing
    if (onMillionAlertToggle) {
      trackFeatureUsage('million_alert_toggle', {
        enabled: !millionAlertEnabled
      });
      onMillionAlertToggle();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="sound-toggle-wrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleWrapperClick}
    >
      <button
        className={`sound-toggle ${enabled ? 'sound-on' : 'sound-off'} ${millionAlertEnabled ? 'million-alert-on' : ''}`}
        onClick={handleToggle}
        aria-label={`Toggle ${label} sound`}
      >
        {/* Sound ON icon */}
        <svg
          className="on"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          width="20px"
          height="20px"
        >
          <path d="M2 16.0001H5.88889L11.1834 20.3319C11.2727 20.405 11.3846 20.4449 11.5 20.4449C11.7761 20.4449 12 20.2211 12 19.9449V4.05519C12 3.93977 11.9601 3.8279 11.887 3.73857C11.7121 3.52485 11.3971 3.49335 11.1834 3.66821L5.88889 8.00007H2C1.44772 8.00007 1 8.44778 1 9.00007V15.0001C1 15.5524 1.44772 16.0001 2 16.0001ZM23 12C23 15.292 21.5539 18.2463 19.2622 20.2622L17.8445 18.8444C19.7758 17.1937 21 14.7398 21 12C21 9.26016 19.7758 6.80629 17.8445 5.15557L19.2622 3.73779C21.5539 5.75368 23 8.70795 23 12ZM18 12C18 10.0883 17.106 8.38548 15.7133 7.28673L14.2842 8.71584C15.3213 9.43855 16 10.64 16 12C16 13.36 15.3213 14.5614 14.2842 15.2841L15.7133 16.7132C17.106 15.6145 18 13.9116 18 12Z"></path>
        </svg>

        {/* Sound OFF icon */}
        <svg
          className="off"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          width="20px"
          height="20px"
        >
          <path d="M5.88889 16.0001H2C1.44772 16.0001 1 15.5524 1 15.0001V9.00007C1 8.44778 1.44772 8.00007 2 8.00007H5.88889L11.1834 3.66821C11.3971 3.49335 11.7121 3.52485 11.887 3.73857C11.9601 3.8279 12 3.93977 12 4.05519V19.9449C12 20.2211 11.7761 20.4449 11.5 20.4449C11.3846 20.4449 11.2727 20.405 11.1834 20.3319L5.88889 16.0001ZM20.4142 12.0001L23.9497 15.5356L22.5355 16.9498L19 13.4143L15.4645 16.9498L14.0503 15.5356L17.5858 12.0001L14.0503 8.46454L15.4645 7.05032L19 10.5859L22.5355 7.05032L23.9497 8.46454L20.4142 12.0001Z"></path>
        </svg>
      </button>

      {/* Tooltip with $1M alert toggle */}
      {showTooltip && (
        <div
          className={`sound-tooltip ${hideTooltipOnMobile ? 'd-none d-md-block' : ''}`}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Close button for mobile */}
          {isTouchDevice && (
            <button
              className="sound-tooltip-close"
              onClick={() => setShowTooltip(false)}
              aria-label="Close"
            >
              ✕
            </button>
          )}
          {tooltipContent && (
            <div dangerouslySetInnerHTML={{ __html: tooltipContent }} />
          )}

          {/* Long/Short Sound Toggle */}
          {showMillionAlertToggle && (
            <div className="million-alert-section">
              <div className="million-alert-header">
                <span className="million-alert-title">{t('longShortSound.title')}</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={millionAlertEnabled}
                    onChange={handleMillionAlertToggle}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <p className="million-alert-hint">
                {t('longShortSound.hint')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SoundToggle;
